import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { FiX, FiPlus, FiEdit2, FiTrash2, FiDownload, FiUpload, FiArrowUp, FiArrowDown, FiEye, FiEyeOff } from 'react-icons/fi';

export interface ChatChip {
  id: string;
  label: string;
  prompt: string;
  isDefault?: boolean;
  hidden?: boolean;
}

const STORAGE_KEY = 'tpp-work-chat-chips';

// Shared prompt used across admin/persona/work — triggers a full capabilities briefing
// tuned to the current chat surface (real tools only, no inventions).
export const CAPABILITIES_PROMPT = `Give me a tight capabilities briefing — the kind a new teammate would pin to their second monitor.

**Structure:**

1. **Who I am, here.** One sentence on your role in *this* chat surface.

2. **What I bring.** If you have tools, group your ACTUAL available tools into 4–7 functional buckets with emoji headers — each tool with a one-sentence blurb, destructive ones flagged inline. If you don't have tools, replace this section with your domain expertise, signature moves, and what distinguishes your perspective.

3. **Use-case playbook.** 6–10 representative scenarios as \`"natural intent" → how I'd approach it\`. Prefer breadth (discovery, ops, analysis, synthesis, creative) over exhausting one category.

4. **How to get the most out of me.** 6–10 sharp tips. Cover what applies: natural language over function names, chaining multiple asks, dry-runs for destructive ops, name→ID resolution, synthesis over raw dumps, output-format requests, discovery/introspection, any hierarchy or convention specific to this surface.

5. **Golden rule.** One line — thinking partner, not command parser.

6. **Hook.** End with one pointed question that invites the next move.

**Rules:**
- No inventions — real tools or real expertise only.
- Scannable: tables or tight bullets, zero paragraph walls.
- Surface-specific: tailor every word to this chat, not a generic assistant.
- Earn every word. If a tip could fit any assistant anywhere, cut it.`;

// Default chips that ship with the app
export const DEFAULT_CHIPS: ChatChip[] = [
  { id: 'work-capabilities', label: '🎭 Capabilities', prompt: CAPABILITIES_PROMPT, isDefault: true },
  { id: 'dashboard', label: '📊 Dashboard', prompt: `Give me a comprehensive executive work dashboard. Call admin_work_summary, admin_work_attention, and admin_standup_summary to get full data. Then present:

1. **Health Score** — the overall health score prominently with a brief assessment
2. **Status Distribution** — a mermaid pie chart showing ALL task statuses (todo, in_progress, in_review, blocked, completed)
3. **Velocity Trend** — this week vs last week completions with trend arrow
4. **Goals Overview** — each goal with its status, progress %, and project count
5. **Risk Board** — overdue tasks (with days overdue), blocked items, and unassigned critical work in a prioritized list
6. **Project Health** — a mermaid gantt chart showing active projects with their timelines, highlighting overdue and at-risk items in red
7. **Upcoming Deadlines** — tasks due in the next 7 days as a table
8. **Team Pulse** — who has the most in-progress work and who has blocked items

Make it visually rich with mermaid diagrams, emoji indicators, and clear section headers. This is the daily command center view.`, isDefault: true },
  { id: 'attention', label: '🔥 Attention', prompt: `What needs my attention RIGHT NOW? Call admin_work_attention and admin_list_work_items to get full data. Present:

1. **🚨 Critical Alert Count** — total items needing action, big and bold
2. **Overdue Tasks** — each with title, days overdue, assignee (or UNASSIGNED in red), and priority. Sort by most overdue first.
3. **Blocked Items** — each with title, what's blocking it (if known), and who owns it
4. **Orphan Goals** — goals with no projects underneath (strategic gaps)
5. **Unassigned Critical** — critical/high priority tasks with no assignee
6. **Risk Pie Chart** — a mermaid pie chart showing the breakdown: overdue vs blocked vs unassigned vs orphan goals
7. **Recommended Actions** — numbered priority list of exactly what to do first, second, third

Use 🔴 for critical, 🟡 for warning, and format as a triage report. Be direct and actionable.`, isDefault: true },
  { id: 'standup', label: '📋 Standup', prompt: `Give me a full weekly standup report. Call admin_standup_summary and admin_work_summary. Present:

1. **Velocity Dashboard** — this week vs last week completions, trend arrow (↑/↓/→), percentage change
2. **Completion Timeline** — a mermaid gantt chart showing tasks completed this week with their completion dates, plus in-progress and upcoming tasks
3. **✅ Completed** — each task with completion date, project context, and who did it
4. **🔄 In Progress** — each with project, assignee, and expected completion
5. **🚫 Blocked** — each with what's blocking and impact assessment
6. **📅 Coming Up** — tasks due in the next 7 days as a deadline table
7. **Status Pie** — a mermaid pie chart of current task distribution
8. **Sprint Health** — one-paragraph narrative: are we on track? what's the biggest risk? what should we celebrate?

Make it presentation-ready for a team standup meeting.`, isDefault: true },
  { id: 'burndown', label: '📈 Burndown', prompt: `Show me a comprehensive burndown analysis. Call admin_work_burndown for the data. Present:

1. **Progress Bar** — visual X/Y tasks completed with percentage
2. **Velocity Metrics** — daily average, peak day, zero-completion days
3. **Completion Pie** — a mermaid pie chart: completed vs in-progress vs remaining
4. **Daily Activity Chart** — a mermaid xychart-beta bar chart showing daily completions over 14 days
5. **Daily Activity Table** — markdown table with date, completed count, and running total
6. **Scope Analysis** — tasks created vs completed in the period (scope creep indicator)
7. **Projection** — at current velocity, how many days to complete remaining work?
8. **Trend Assessment** — is velocity increasing, decreasing, or flat? What's driving it?

Include both mermaid charts AND the data table. Be analytical.`, isDefault: true },
  { id: 'leaderboard', label: '🏆 Leaderboard', prompt: `Show me the team productivity leaderboard. Call admin_work_leaderboard. Present:

1. **🥇🥈🥉 Podium** — top 3 contributors with completion counts, formatted as a medal podium
2. **Full Rankings Table** — all contributors ranked by completions, showing: rank, name, completed, in-progress, blocked, total assigned
3. **Workload Pie** — a mermaid pie chart showing how total assigned work is distributed across the team
4. **Completion Pie** — a mermaid pie chart showing who completed the most this period
5. **Blocked Spotlight** — who has the most blocked items and what they are
6. **Unbalanced Load** — flag anyone with significantly more or fewer tasks than average
7. **MVP Highlight** — who had the biggest impact this week and why

Make it fun and motivating, not punitive. Celebrate achievements.`, isDefault: true },
  { id: 'forecast', label: '🔮 Forecast', prompt: `Forecast the Q2 Product Launch goal. Call admin_work_forecast and admin_list_work_items for full data. Present:

1. **Status Banner** — ON TRACK / AT RISK / CRITICAL in big bold text with the right emoji
2. **Progress** — X/Y tasks completed with percentage bar
3. **Timeline Gantt** — a mermaid gantt chart showing: completed tasks (done), in-progress (active), remaining tasks, and milestone markers for Due Date and Expected Completion
4. **Three Estimates** — 🟢 Optimistic, 🟡 Expected, 🔴 Pessimistic with dates and days until each
5. **Deadline Comparison** — expected completion vs due date, with days delta
6. **Velocity Required** — what daily velocity is needed to hit the deadline vs current velocity
7. **Critical Path** — which tasks are on the critical path and who owns them
8. **Risk Mitigation** — specific recommendations to get back on track (if at risk)

This is for executive stakeholder communication. Be precise with dates and numbers.`, isDefault: true },
  { id: 'scaffold', label: '🎯 Scaffold', prompt: `Help me scaffold a comprehensive new goal. Use admin_scaffold_goal to create the full hierarchy in ONE call. Create:

**Goal:** "Platform Security Hardening" (priority: critical, due: 6 weeks from now)

**Projects & Tasks:**
1. **Authentication Review** — audit OAuth flows, review session management, test password policies, implement MFA enforcement
2. **Infrastructure Audit** — scan for CVEs, review firewall rules, audit SSL certificates, check container security
3. **Penetration Testing** — plan test scope, execute external pentest, execute internal pentest, remediation tracking

Each task should have appropriate priority (critical/high). After creation, show a summary with a mermaid gantt chart of the planned timeline and a pie chart of task distribution across projects.`, isDefault: true },
  { id: 'heartbeat-preview', label: '💓 Heartbeat Preview', prompt: `Dry run a heartbeat pulse for the last 3 days. Call dry_run_pulse with pulse_type="heartbeat" and date_range_days=3.

Show me the full briefing it would generate:
1. **Preview Content** — the complete narrated briefing text
2. **Data Sources** — a summary of what data was collected (event counts, wins, attention items, session activity)
3. **Token Estimate** — how many tokens this run would consume and the estimated cost
4. **Tier** — which tier is currently configured

This is a dry run — nothing is saved. Use this to preview what tonight's scheduled briefing will look like.`, isDefault: true },
  { id: 'unassigned', label: '🔄 Unassigned', prompt: `Show me ALL unassigned work. Call admin_list_work_items with unassigned=True. Present:

1. **Unassigned Count** — total number, prominently displayed
2. **Priority Pie** — a mermaid pie chart showing critical vs high vs medium vs low distribution
3. **🔴 Critical Unassigned** — list each with title, project, due date. These need IMMEDIATE assignment.
4. **🟡 High Priority** — list each with context
5. **Medium & Low** — grouped summary
6. **By Project** — which projects have the most unassigned work (potential understaffing)
7. **Overdue & Unassigned** — the worst combo: tasks past due with nobody on them
8. **Assignment Recommendations** — based on current team workload (from leaderboard data), suggest who should pick up what

This is for a resource allocation meeting. Be specific about what needs owners.`, isDefault: true },
];

export function loadChips(): ChatChip[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return DEFAULT_CHIPS.map(c => ({ ...c }));
}

export function saveChips(chips: ChatChip[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(chips));
}

interface Props {
  open: boolean;
  onClose: () => void;
  chips: ChatChip[];
  onChipsChange: (chips: ChatChip[]) => void;
}

const ChipManager: React.FC<Props> = ({ open, onClose, chips, onChipsChange }) => {
  const { darkMode } = useTheme();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editPrompt, setEditPrompt] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newPrompt, setNewPrompt] = useState('');
  const [importText, setImportText] = useState('');
  const [showImport, setShowImport] = useState(false);

  if (!open) return null;

  const handleSave = (chips: ChatChip[]) => {
    onChipsChange(chips);
    saveChips(chips);
  };

  const handleAdd = () => {
    if (!newLabel.trim() || !newPrompt.trim()) return;
    const id = `custom-${Date.now()}`;
    const updated = [...chips, { id, label: newLabel.trim(), prompt: newPrompt.trim() }];
    handleSave(updated);
    setNewLabel(''); setNewPrompt(''); setShowAdd(false);
  };

  const handleDelete = (id: string) => {
    handleSave(chips.filter(c => c.id !== id));
  };

  const handleToggleHidden = (id: string) => {
    handleSave(chips.map(c => c.id === id ? { ...c, hidden: !c.hidden } : c));
  };

  const handleMove = (id: string, direction: 'up' | 'down') => {
    const idx = chips.findIndex(c => c.id === id);
    if (idx < 0) return;
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= chips.length) return;
    const updated = [...chips];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    handleSave(updated);
  };

  const handleStartEdit = (chip: ChatChip) => {
    setEditingId(chip.id);
    setEditLabel(chip.label);
    setEditPrompt(chip.prompt);
  };

  const handleSaveEdit = () => {
    if (!editingId || !editLabel.trim() || !editPrompt.trim()) return;
    handleSave(chips.map(c => c.id === editingId ? { ...c, label: editLabel.trim(), prompt: editPrompt.trim() } : c));
    setEditingId(null);
  };

  const handleExport = () => {
    const exportData = chips.filter(c => !c.hidden);
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'work-chat-chips.json'; a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = () => {
    try {
      const imported: ChatChip[] = JSON.parse(importText);
      if (!Array.isArray(imported)) throw new Error('Not an array');
      const merged = [...chips];
      for (const chip of imported) {
        if (!chip.label || !chip.prompt) continue;
        const existing = merged.find(c => c.id === chip.id);
        if (!existing) {
          merged.push({ ...chip, id: chip.id || `imported-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, isDefault: false });
        }
      }
      handleSave(merged);
      setImportText(''); setShowImport(false);
    } catch {
      alert('Invalid JSON. Expected an array of {id, label, prompt} objects.');
    }
  };

  const handleResetDefaults = () => {
    handleSave(DEFAULT_CHIPS.map(c => ({ ...c })));
  };

  const inputClass = `w-full px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none`;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className={`w-full max-w-2xl max-h-[80vh] rounded-xl shadow-2xl flex flex-col ${darkMode ? 'bg-gray-900' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} shrink-0`}>
          <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Manage Quick Action Chips</h2>
          <button onClick={onClose} className={`p-1 rounded ${darkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Chip list */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {chips.map((chip, idx) => (
            <div key={chip.id} className={`flex items-start gap-2 p-3 rounded-lg ${chip.hidden ? 'opacity-40' : ''} ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
              {editingId === chip.id ? (
                <div className="flex-1 space-y-2">
                  <input value={editLabel} onChange={e => setEditLabel(e.target.value)} className={inputClass} placeholder="Label (e.g., 📊 Dashboard)" />
                  <textarea value={editPrompt} onChange={e => setEditPrompt(e.target.value)} rows={3} className={`${inputClass} resize-none`} placeholder="Prompt..." />
                  <div className="flex gap-2">
                    <button onClick={handleSaveEdit} className="px-3 py-1 rounded text-xs bg-violet-600 text-white hover:bg-violet-500">Save</button>
                    <button onClick={() => setEditingId(null)} className={`px-3 py-1 rounded text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{chip.label}</div>
                    <p className={`text-xs mt-0.5 truncate ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{chip.prompt}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => handleMove(chip.id, 'up')} disabled={idx === 0} className={`p-1 rounded ${darkMode ? 'hover:bg-gray-700 text-gray-500' : 'hover:bg-gray-200 text-gray-400'} disabled:opacity-20`}>
                      <FiArrowUp className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => handleMove(chip.id, 'down')} disabled={idx === chips.length - 1} className={`p-1 rounded ${darkMode ? 'hover:bg-gray-700 text-gray-500' : 'hover:bg-gray-200 text-gray-400'} disabled:opacity-20`}>
                      <FiArrowDown className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => handleToggleHidden(chip.id)} className={`p-1 rounded ${darkMode ? 'hover:bg-gray-700 text-gray-500' : 'hover:bg-gray-200 text-gray-400'}`} title={chip.hidden ? 'Show' : 'Hide'}>
                      {chip.hidden ? <FiEyeOff className="w-3.5 h-3.5" /> : <FiEye className="w-3.5 h-3.5" />}
                    </button>
                    <button onClick={() => handleStartEdit(chip)} className={`p-1 rounded ${darkMode ? 'hover:bg-gray-700 text-gray-500' : 'hover:bg-gray-200 text-gray-400'}`}>
                      <FiEdit2 className="w-3.5 h-3.5" />
                    </button>
                    {!chip.isDefault && (
                      <button onClick={() => handleDelete(chip.id)} className="p-1 rounded hover:bg-red-900/30 text-gray-500 hover:text-red-400">
                        <FiTrash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}

          {/* Add new chip form */}
          {showAdd && (
            <div className={`p-3 rounded-lg border-2 border-dashed ${darkMode ? 'border-violet-800 bg-gray-800/50' : 'border-violet-200 bg-violet-50/50'}`}>
              <input value={newLabel} onChange={e => setNewLabel(e.target.value)} className={inputClass} placeholder="Label (e.g., 🚀 Sprint Review)" />
              <textarea value={newPrompt} onChange={e => setNewPrompt(e.target.value)} rows={3} className={`${inputClass} mt-2 resize-none`} placeholder="Prompt that will be sent to the AI..." />
              <div className="flex gap-2 mt-2">
                <button onClick={handleAdd} disabled={!newLabel.trim() || !newPrompt.trim()} className="px-3 py-1.5 rounded text-xs bg-violet-600 text-white hover:bg-violet-500 disabled:opacity-40">Add Chip</button>
                <button onClick={() => { setShowAdd(false); setNewLabel(''); setNewPrompt(''); }} className={`px-3 py-1.5 rounded text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Cancel</button>
              </div>
            </div>
          )}

          {/* Import area */}
          {showImport && (
            <div className={`p-3 rounded-lg border-2 border-dashed ${darkMode ? 'border-teal-800 bg-gray-800/50' : 'border-teal-200 bg-teal-50/50'}`}>
              <textarea value={importText} onChange={e => setImportText(e.target.value)} rows={4} className={`${inputClass} resize-none font-mono text-xs`} placeholder='Paste JSON array: [{"label": "...", "prompt": "..."}]' />
              <div className="flex gap-2 mt-2">
                <button onClick={handleImport} disabled={!importText.trim()} className="px-3 py-1.5 rounded text-xs bg-teal-600 text-white hover:bg-teal-500 disabled:opacity-40">Import</button>
                <button onClick={() => { setShowImport(false); setImportText(''); }} className={`px-3 py-1.5 rounded text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Cancel</button>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className={`flex items-center justify-between px-6 py-3 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} shrink-0`}>
          <div className="flex gap-2">
            {!showAdd && <button onClick={() => { setShowAdd(true); setShowImport(false); }} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs bg-violet-600 text-white hover:bg-violet-500">
              <FiPlus className="w-3 h-3" /> New Chip
            </button>}
            <button onClick={handleExport} className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs ${darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              <FiDownload className="w-3 h-3" /> Export
            </button>
            {!showImport && <button onClick={() => { setShowImport(true); setShowAdd(false); }} className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs ${darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              <FiUpload className="w-3 h-3" /> Import
            </button>}
          </div>
          <button onClick={handleResetDefaults} className={`text-xs ${darkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}>
            Reset to defaults
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChipManager;
